import {
  eachDayOfInterval,
  startOfMonth,
  endOfMonth,
  addDays,
  format,
} from 'date-fns'
import { DaysOfWeek, CalendarFormat } from '../useCalendar/types'
import { getFirstItemOfArray, getLastItemOfArray } from './helpers'

export const isExtraDay = (firstDay: Date, secondDay: Date): boolean => isExtraDayBefore(firstDay, secondDay) || isExtraDayAfter(firstDay, secondDay)

export const isExtraDayBefore = (firstDay: Date, secondDay: Date): boolean => firstDay.getMonth() < secondDay.getMonth()

export const isExtraDayAfter = (firstDay: Date, secondDay: Date): boolean => firstDay.getMonth() > secondDay.getMonth()

export const normalizeDate = (date: Date) => {
  // Date() objects need to be cloned to avoid mutating objects coming from external components
  const _date = new Date(date)
  _date.setHours(12)
  return date
}

export const daysInWeek = (week: Date, startingDayOfWeek: DaysOfWeek): Date[] => {
  const start = firstDayOfWeek(week, startingDayOfWeek)
  const end = lastDayOfWeek(week, startingDayOfWeek)
  const interval = eachDayOfInterval({ start, end })
  interval.pop()
  return interval;
}

export const firstDayOfWeek = (week: Date, startingDayOfWeek: DaysOfWeek) => {
  const normalizedDate = normalizeDate(week);
  const decreaseSum = getDaysBefore(normalizedDate, startingDayOfWeek)

  return addDays(normalizedDate, decreaseSum * -1)
}

export const lastDayOfWeek = (week: Date, startingDayOfWeek: DaysOfWeek) => {
  const normalizedDate = normalizeDate(week);
  const increaseSum = getDaysBefore(normalizedDate, startingDayOfWeek)

  return addDays(normalizedDate, 7 - increaseSum)
}

export const daysInMonth = (month: Date, startingDayOfWeek: DaysOfWeek): Date[] => {
  const first = startOfMonth(month);
  const daysBefore = getDaysBefore(first, startingDayOfWeek);
  const start = addDays(first, (daysBefore) * -1);
  const last = endOfMonth(month);
  const daysAfter = getDaysAfter(last, startingDayOfWeek);
  const end = addDays(last, daysAfter);

  return eachDayOfInterval({start, end})
}


const getDaysBefore = (firstDay: Date, startingDayOfWeek: DaysOfWeek) => (firstDay.getDay() + 7 - startingDayOfWeek) % 7

const getDaysAfter = (lastDay: Date, startingDayOfWeek: DaysOfWeek) => {
  const invertedStartingWeekday = 8 - startingDayOfWeek;

  let daysAfter = 7 - ((lastDay.getDay() + invertedStartingWeekday) % 7);
  if (daysAfter == 8) {
    daysAfter = 1;
  }
  return daysAfter;
}


export const getFirstDay = (date: Date, visibleDays: Date[], includeInvisible: boolean, calendarFormat: CalendarFormat): Date => {
  if (calendarFormat === CalendarFormat.month && !includeInvisible) {
    return startOfMonth(date);
  }

  return getFirstItemOfArray(visibleDays);
}


export const getLastDay = (date: Date, visibleDays: Date[], includeInvisible: boolean, calendarFormat: CalendarFormat): Date => {
  if (calendarFormat === CalendarFormat.month && !includeInvisible) {
    return endOfMonth(date);
  }

  return getLastItemOfArray(visibleDays)
}


export const getCalendarFormat = (availableCalendarFormats: CalendarFormat[], currentCalendarFormat: CalendarFormat) => {
  let id = availableCalendarFormats.indexOf(currentCalendarFormat);
  id = (id + 1) % availableCalendarFormats.length;

  return availableCalendarFormats[id];
}


export const previousWeek = (week: Date) => addDays(week, 7 * -1)

export const nextWeek = (week: Date) => addDays(week, 7)

export const previousMonth = (month: Date) => {
  if (month.getMonth() == 1) {
    return new Date(month.getFullYear() - 1, 12);
  }
  return new Date(month.getFullYear(), month.getMonth() - 1);
  
}

export const nextMonth = (month: Date) => {
  if (month.getMonth() === 12) {
    return new Date(month.getFullYear() + 1, 1);
  }
  return new Date(month.getFullYear(), month.getMonth() + 1);
}


export const isWeekend = (day: Date, weekendDays: string[]) => {
  const dayOfWeek = format(day, 'EEEE')
  return weekendDays.includes(dayOfWeek.toLowerCase())
}
